/**
 * The real, standalone run for one catalog tool outside the full pipeline.
 *
 * `ToolLaunchDialog`'s Continue button opens this once a volume is chosen
 * (or, for DICOM import, immediately — see `skipFirstVolume` below), for
 * every Tools-menu entry that is not SAM/MedSAM-style interactive (see
 * `tools/segment/InteractiveSegPanel.tsx`, which this mirrors). The honest
 * part of this panel matters more than the running part: brain extraction
 * (SynthStrip), the four `itk.*` tools (real, officially published
 * `@itk-wasm/*` pipelines — see `tools/itk/*Client.ts`), and the two
 * `dwiTool` stages (real ANTs registration + real DAGMNet DWI segmentation,
 * same computation the main pipeline runs — see `lib/dwi-pipeline.ts`) have
 * a real in-browser implementation today — see `wasm-engine/engine.ts`'s own
 * "Scope of this pass" note. Every other catalog tool still opens this same
 * popup, but shows plainly why it cannot run here yet instead of silently
 * jumping to the View tab and doing nothing, which is the bug this replaces.
 */
import * as React from "react";
import { Loader2, Play, Save, Upload } from "lucide-react";

import { Button } from "@/components/ui/primitives";
import { DialogFooter, DialogOverlay, DialogPanel, DialogTitle, dialogPrimaryButtonClass, dialogSecondaryButtonClass } from "@/components/ui/dialog";
import { getAssetUrl } from "@/ipc/api";
import type { CaseNode } from "@/ipc/contract";
import { maskToVol, niftiGz } from "@/lib/export";
import type { NiftiVolume } from "@/lib/nifti";
import { ab, runWorker } from "@/lib/worker-client";
import { cn } from "@/lib/utils";
import { useCurrentCase } from "@/store/appStore";
import { compareVolumes, type CompareResult } from "@/tools/itk/compareImagesClient";
import { importDicomSeries } from "@/tools/itk/dicomClient";
import { downsampleVolume } from "@/tools/itk/downsampleClient";
import { interpolateSegmentationSlices } from "@/tools/itk/morphologicalContourInterpolationClient";
import { runDwiRegistration, runDwiSegmentation } from "@/lib/dwi-pipeline";
import { OVERLAY_PALETTE, SliceCanvas } from "@/viewer/SliceCanvas";
import { loadVolume } from "@/viewer/volumeCache";
import { nodeAbsPath } from "@/viewer/viewerStore";
import { registerToolOutput } from "@/wasm-engine/engine";
import { isBrainExtraction, toolById, type ToolDef } from "../catalog";
import { useStageRunStore } from "./store";

import { appPath } from "../../lib/assets";
type Phase =
  | "loading-volume"
  | "picking-files"
  | "intro"
  | "running"
  | "ready"
  | "saved"
  | "unavailable"
  | "error";

interface SkullStripResult {
  mask: ArrayBuffer;
  dims: number[];
  brainVoxels: number;
  totalMs: number;
}

/** Same worker, same ONNX model `runSkullStrip` in `wasm-engine/engine.ts` uses
 *  for the main pipeline -- run here directly, standalone, the same way
 *  `InteractiveSegPanel` talks to SAM's session without going through a job. */
async function runSkullStripStandalone(
  vol: NiftiVolume,
  onProgress: (stage: string) => void,
): Promise<SkullStripResult> {
  const worker = new Worker(appPath("workers/synthstrip.worker.js"), { type: "module" });
  try {
    return await runWorker<
      { voxels: ArrayBuffer; dims: number[]; pixdim: number[]; affine: ArrayBuffer },
      SkullStripResult
    >(
      worker,
      {
        voxels: ab(vol.voxels),
        dims: vol.dims as unknown as number[],
        pixdim: vol.pixdim as unknown as number[],
        affine: ab(vol.affine),
      },
      onProgress,
    );
  } finally {
    worker.terminate();
  }
}

function unavailableNote(tool: ToolDef): string {
  if (tool.status === "future" && tool.comingSoon) return tool.comingSoon;
  if (tool.op) {
    return `${tool.engine} operations like this run through the desktop app's Docker backend and are not wired into the browser build yet. Brain extraction (SynthStrip) and the Image Processing tools are what currently run fully in your browser.`;
  }
  if (tool.stage) {
    return `The "${tool.stage.id}" OpenADS stage hasn't been wired into the browser build of ADS Desk yet -- see openads-wasm/app for the working version of this computation.`;
  }
  if (tool.inspect === "header") {
    return "Reading the real NIfTI header from this popup isn't wired into the browser build yet.";
  }
  return "This isn't a standalone operation in the browser build yet.";
}

function volumeToUrl(vol: NiftiVolume): Promise<string> {
  return niftiGz(vol).then((bytes) => URL.createObjectURL(new Blob([bytes as BlobPart], { type: "application/gzip" })));
}

export function StageRunPanel() {
  const open = useStageRunStore((s) => s.open);
  const toolId = useStageRunStore((s) => s.toolId);
  const nodeId = useStageRunStore((s) => s.nodeId);
  const close = useStageRunStore((s) => s.close);
  const manifest = useCurrentCase();
  const tool = toolById(toolId ?? "");

  const [phase, setPhase] = React.useState<Phase>("loading-volume");
  const [progress, setProgress] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [vol, setVol] = React.useState<NiftiVolume | null>(null);
  const [baseUrl, setBaseUrl] = React.useState<string | null>(null);
  const [resultUrl, setResultUrl] = React.useState<string | null>(null);
  const [resultVol, setResultVol] = React.useState<NiftiVolume | null>(null);
  const [statusLine, setStatusLine] = React.useState("");
  const [savedPath, setSavedPath] = React.useState<string | null>(null);
  const [shrinkFactor, setShrinkFactor] = React.useState(2);
  // Second volume picker: compareImages' baseline, register's ADC pair.
  const [baselineNodeId, setBaselineNodeId] = React.useState<string>("");
  // Third, optional volume picker: segmentStroke's registered brain mask.
  const [maskNodeId, setMaskNodeId] = React.useState<string>("");
  const [dicomFiles, setDicomFiles] = React.useState<File[]>([]);
  // Additional real outputs alongside the one shown in the viewer (register
  // produces DWI+ADC+mask, not just one volume) -- saved together.
  const [extraResults, setExtraResults] = React.useState<{ name: string; vol: NiftiVolume }[]>([]);
  const objectUrlsRef = React.useRef<string[]>([]);

  const revokeObjectUrls = React.useCallback(() => {
    for (const u of objectUrlsRef.current) URL.revokeObjectURL(u);
    objectUrlsRef.current = [];
  }, []);
  const track = React.useCallback((url: string) => {
    objectUrlsRef.current.push(url);
    return url;
  }, []);

  const reset = React.useCallback(() => {
    revokeObjectUrls();
    setPhase("loading-volume");
    setProgress("");
    setError(null);
    setVol(null);
    setBaseUrl(null);
    setResultUrl(null);
    setResultVol(null);
    setStatusLine("");
    setSavedPath(null);
    setBaselineNodeId("");
    setMaskNodeId("");
    setDicomFiles([]);
    setExtraResults([]);
  }, [revokeObjectUrls]);

  // Load the chosen volume as soon as the panel opens, same as
  // `InteractiveSegPanel` -- this is a local read of the reader's own
  // already-selected file, not a network fetch, so it does not wait on
  // anything else. DICOM import is the one tool with no volume to load yet.
  React.useEffect(() => {
    if (!open) return;
    reset();
    if (tool?.itkTool === "dicomImport") {
      setPhase("picking-files");
      return;
    }
    let live = true;
    (async () => {
      const node = manifest ? [...manifest.images, ...manifest.masks].find((n) => n.id === nodeId) : null;
      const abs = node ? nodeAbsPath(manifest, node) : null;
      if (!abs) {
        if (live) {
          setError("Could not resolve that volume's file.");
          setPhase("error");
        }
        return;
      }
      try {
        const { url } = await getAssetUrl(abs);
        const v = await loadVolume(url);
        if (!live) return;
        setVol(v);
        setBaseUrl(url);
        setPhase(tool && (isBrainExtraction(tool) || tool.itkTool || tool.dwiTool) ? "intro" : "unavailable");
      } catch (err) {
        if (live) {
          setError(err instanceof Error ? err.message : String(err));
          setPhase("error");
        }
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, nodeId, toolId]);

  React.useEffect(() => {
    if (!open) revokeObjectUrls();
  }, [open, revokeObjectUrls]);

  const caseVolumes: CaseNode[] = manifest ? [...manifest.images, ...manifest.masks] : [];
  const baselineCandidates = caseVolumes.filter((n) => n.id !== nodeId);

  const run = async () => {
    if (!tool) return;
    setPhase("running");
    setError(null);
    try {
      if (isBrainExtraction(tool)) {
        if (!vol) return;
        setProgress("");
        const result = await runSkullStripStandalone(vol, (stage) => setProgress(stage));
        const maskVol = maskToVol(new Uint8Array(result.mask), vol.dims, vol.pixdim, vol.affine);
        setResultVol(maskVol);
        setResultUrl(track(await volumeToUrl(maskVol)));
        setStatusLine(`${result.brainVoxels.toLocaleString()} brain voxels`);
        setPhase("ready");
        return;
      }

      if (tool.itkTool === "downsample") {
        if (!vol) return;
        const out = await downsampleVolume(vol, [shrinkFactor, shrinkFactor, shrinkFactor]);
        setResultVol(out);
        setResultUrl(track(await volumeToUrl(out)));
        setBaseUrl(null); // show the downsampled volume itself, not the original, as the base
        setStatusLine(`${out.dims.join(" × ")} (was ${vol.dims.join(" × ")})`);
        setPhase("ready");
        return;
      }

      if (tool.itkTool === "morphInterp") {
        if (!vol) return;
        const out = await interpolateSegmentationSlices(vol);
        setResultVol(out);
        setResultUrl(track(await volumeToUrl(out)));
        setStatusLine("Interpolated volume shown as overlay on the original.");
        setPhase("ready");
        return;
      }

      if (tool.itkTool === "compareImages") {
        if (!vol || !manifest) return;
        const baselineNode = caseVolumes.find((n) => n.id === baselineNodeId);
        if (!baselineNode) {
          setError("Pick a baseline volume to compare against.");
          setPhase("intro");
          return;
        }
        const baselineAbs = nodeAbsPath(manifest, baselineNode);
        if (!baselineAbs) {
          setError("Could not resolve the baseline volume's file.");
          setPhase("intro");
          return;
        }
        const { url: baselineUrl } = await getAssetUrl(baselineAbs);
        const baselineVol = await loadVolume(baselineUrl);
        const cmp: CompareResult = await compareVolumes(vol, baselineVol, 0);
        setResultVol(cmp.differenceImage);
        setResultUrl(track(await volumeToUrl(cmp.differenceImage)));
        setStatusLine(
          cmp.metrics.almostEqual
            ? "Images match within tolerance."
            : `${cmp.metrics.numberOfPixelsWithDifferences.toLocaleString()} differing voxels, mean diff ${cmp.metrics.meanDifference.toFixed(3)}, max ${cmp.metrics.maximumDifference.toFixed(3)}.`,
        );
        setPhase("ready");
        return;
      }

      if (tool.dwiTool === "register") {
        if (!vol || !manifest) return;
        const adcNode = caseVolumes.find((n) => n.id === baselineNodeId);
        if (!adcNode) {
          setError("Pick the matching ADC volume to register alongside this DWI.");
          setPhase("intro");
          return;
        }
        const adcAbs = nodeAbsPath(manifest, adcNode);
        if (!adcAbs) {
          setError("Could not resolve the ADC volume's file.");
          setPhase("intro");
          return;
        }
        const { url: adcUrl } = await getAssetUrl(adcAbs);
        const adcVol = await loadVolume(adcUrl);
        setProgress("skull-stripping (SynthStrip)");
        const ss = await runSkullStripStandalone(vol, (s) => setProgress(`skull strip: ${s}`));
        const reg = await runDwiRegistration(vol, adcVol, new Uint8Array(ss.mask), (s) => setProgress(s));
        // This tool always runs SyN; the fallbacks only satisfy the optional types.
        const regDwi = reg.registeredDwi ?? reg.affineDwi;
        const regAdc = reg.registeredAdc ?? reg.affineAdc;
        const regMask = reg.registeredMask ?? reg.affineMaskBinary;
        setResultVol(regDwi);
        setResultUrl(track(await volumeToUrl(regDwi)));
        setBaseUrl(null); // show the registered (affine+SyN) DWI itself, not the raw input, as the base
        setExtraResults([
          { name: "registered-adc.nii.gz", vol: regAdc },
          { name: "registered-mask.nii.gz", vol: maskToVol(regMask, regDwi.dims, regDwi.pixdim, regDwi.affine) },
          // Affine-only (no SyN) — this is the space "Segment Stroke Core" actually
          // needs: the real pipeline's DAGMNet DWI inference runs on _aff, not
          // _affsyn (confirmed against scripts/run_ads_dwi.py's SegmentationInputs;
          // see runDwiSegmentation's doc comment). Surfaced here so a user chaining
          // Register -> Segment via these standalone tools picks the correct input.
          { name: "affine-dwi.nii.gz", vol: reg.affineDwi },
          { name: "affine-adc.nii.gz", vol: reg.affineAdc },
          { name: "affine-mask.nii.gz", vol: maskToVol(reg.affineMaskBinary, reg.affineDwi.dims, reg.affineDwi.pixdim, reg.affineDwi.affine) },
        ]);
        setStatusLine(
          `Registered to ${regDwi.dims.join(" × ")} MNI grid (real ANTs Affine+SyN). ` +
            `For "Segment Stroke Core", use the affine-dwi/affine-adc/affine-mask outputs below, not the ` +
            `registered-* (SyN) ones — that's the space the real DAGMNet model actually expects.`,
        );
        setPhase("ready");
        return;
      }

      if (tool.dwiTool === "segmentStroke") {
        if (!vol || !manifest) return;
        const adcNode = caseVolumes.find((n) => n.id === baselineNodeId);
        if (!adcNode) {
          setError("Pick the matching registered ADC volume.");
          setPhase("intro");
          return;
        }
        const adcAbs = nodeAbsPath(manifest, adcNode);
        if (!adcAbs) {
          setError("Could not resolve the ADC volume's file.");
          setPhase("intro");
          return;
        }
        const { url: adcUrl } = await getAssetUrl(adcAbs);
        const adcVol = await loadVolume(adcUrl);
        let maskArr: Uint8Array | null = null;
        const maskNode = caseVolumes.find((n) => n.id === maskNodeId);
        if (maskNode) {
          const maskAbs = nodeAbsPath(manifest, maskNode);
          if (maskAbs) {
            const { url: maskUrl } = await getAssetUrl(maskAbs);
            const maskVol = await loadVolume(maskUrl);
            maskArr = new Uint8Array(maskVol.voxels.length);
            for (let i = 0; i < maskArr.length; i++) maskArr[i] = maskVol.voxels[i] > 0.5 ? 1 : 0;
          }
        }
        const seg = await runDwiSegmentation(vol, adcVol, maskArr, (s) => setProgress(s));
        const maskVolOut = maskToVol(seg.mask, seg.dims, vol.pixdim, vol.affine);
        setResultVol(maskVolOut);
        setResultUrl(track(await volumeToUrl(maskVolOut)));
        setStatusLine(`${seg.lesionVoxels.toLocaleString()} lesion voxels (real DAGMNet DWI ONNX, ${seg.totalMs}ms)`);
        setPhase("ready");
        return;
      }

      if (tool.itkTool === "dicomImport") {
        if (dicomFiles.length === 0) {
          setError("Choose at least one DICOM file first.");
          setPhase("picking-files");
          return;
        }
        const { volume, sortedFilenames } = await importDicomSeries(dicomFiles);
        setResultVol(volume);
        setResultUrl(track(await volumeToUrl(volume)));
        setStatusLine(`${sortedFilenames.length || dicomFiles.length} file(s) → ${volume.dims.join(" × ")} volume.`);
        setPhase("ready");
        return;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase("error");
    }
  };

  const save = () => {
    if (!resultVol || !tool) return;
    void (async () => {
      const runId = Date.now().toString(36);
      const base = `tools/${tool.id.replace(/\./g, "-")}/${runId}`;
      const resultBytes = await niftiGz(resultVol);
      registerToolOutput(`${base}/result.nii.gz`, new Blob([resultBytes as BlobPart], { type: "application/gzip" }), "nifti");
      for (const extra of extraResults) {
        const bytes = await niftiGz(extra.vol);
        registerToolOutput(`${base}/${extra.name}`, new Blob([bytes as BlobPart], { type: "application/gzip" }), "nifti");
      }
      setSavedPath(`${base}/result.nii.gz${extraResults.length ? ` (+${extraResults.length} more)` : ""}`);
      setPhase("saved");
    })();
  };

  if (!open || !tool) return null;

  const busy = phase === "running" || phase === "loading-volume";
  const isMaskLikeOverlay = isBrainExtraction(tool) || tool.itkTool === "morphInterp" || tool.itkTool === "compareImages" || tool.dwiTool === "segmentStroke";

  return (
    <DialogOverlay align="top" className="z-[76]" onMouseDown={close}>
      <DialogPanel className="max-h-[88vh] w-[680px]" onMouseDown={(e) => e.stopPropagation()}>
        <DialogTitle title={tool.label} subtitle={tool.description} onClose={close} />

        {phase === "error" ? (
          <div className="px-6 pb-6">
            <p className="text-[12px] text-red-700">{error}</p>
            <Button className={cn("mt-3", dialogSecondaryButtonClass)} size="sm" onClick={close}>
              Close
            </Button>
          </div>
        ) : phase === "loading-volume" ? (
          <div className="flex items-center justify-center p-10 text-neutral-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : phase === "unavailable" ? (
          <div className="space-y-4 px-6 pb-6">
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12px] leading-relaxed text-amber-900">
              {unavailableNote(tool)}
            </p>
            <div className="flex justify-end">
              <Button size="sm" className={dialogSecondaryButtonClass} onClick={close}>
                Close
              </Button>
            </div>
          </div>
        ) : phase === "picking-files" ? (
          <div className="space-y-4 px-6 pb-6">
            <p className="text-[12px] leading-relaxed text-ads-text">
              Pick the DICOM file(s) for one series. Nothing is uploaded -- parsing happens entirely in your browser.
            </p>
            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-ads-border-strong px-3.5 py-5 text-[12px] text-ads-muted transition-colors hover:bg-black/[0.03]">
              <Upload className="h-4 w-4" />
              {dicomFiles.length > 0 ? `${dicomFiles.length} file(s) chosen` : "Choose DICOM files…"}
              <input
                type="file"
                multiple
                accept=".dcm,application/dicom"
                className="hidden"
                onChange={(e) => setDicomFiles(Array.from(e.currentTarget.files ?? []))}
              />
            </label>
            {error ? <p className="text-[12px] text-red-700">{error}</p> : null}
            <div className="flex justify-end gap-2">
              <Button size="sm" className={dialogSecondaryButtonClass} onClick={close}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" className={dialogPrimaryButtonClass} disabled={dicomFiles.length === 0} onClick={() => void run()}>
                <Play className="h-3.5 w-3.5" />
                Import
              </Button>
            </div>
          </div>
        ) : phase === "intro" ? (
          <div className="space-y-4 px-6 pb-6">
            <p className="text-[12px] leading-relaxed text-ads-text">
              {isBrainExtraction(tool)
                ? "Runs SynthStrip (ONNX Runtime Web) on the selected volume, entirely in this browser. Nothing is uploaded."
                : tool.dwiTool === "register"
                  ? "Runs SynthStrip for the brain mask, then real ANTs antsRegistration (Affine+SyN) + antsApplyTransforms, entirely in this browser. Several minutes, single-threaded."
                  : tool.dwiTool === "segmentStroke"
                    ? "Runs the real DAGMNet DWI (ch3) ONNX model on an already MNI-registered DWI + ADC pair, entirely in this browser."
                    : "Runs entirely in this browser, via the official itk-wasm pipeline. Nothing is uploaded; the pipeline's .wasm is fetched once, on this click."}
            </p>
            <p className="text-[11.5px] text-ads-muted">Engine: {tool.engine}</p>
            {tool.itkTool === "downsample" ? (
              <label className="flex items-center gap-2 text-[12px] text-ads-text">
                Shrink factor (all axes)
                <input
                  type="number"
                  min={1}
                  max={8}
                  value={shrinkFactor}
                  onChange={(e) => setShrinkFactor(Math.max(1, Math.min(8, Number(e.currentTarget.value) || 1)))}
                  className="w-16 rounded-lg border border-ads-border px-2 py-1"
                />
              </label>
            ) : null}
            {tool.itkTool === "compareImages" || tool.dwiTool === "register" || tool.dwiTool === "segmentStroke" ? (
              <label className="flex flex-col gap-1 text-[12px] text-ads-text">
                {tool.dwiTool === "register" ? "Matching raw ADC volume" : tool.dwiTool === "segmentStroke" ? "Matching registered ADC volume" : "Baseline volume to compare against"}
                <select
                  value={baselineNodeId}
                  onChange={(e) => setBaselineNodeId(e.currentTarget.value)}
                  className="rounded-lg border border-ads-border px-2 py-1"
                >
                  <option value="">Choose…</option>
                  {baselineCandidates.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {tool.dwiTool === "segmentStroke" ? (
              <label className="flex flex-col gap-1 text-[12px] text-ads-text">
                Registered brain mask (optional, improves normalization)
                <select
                  value={maskNodeId}
                  onChange={(e) => setMaskNodeId(e.currentTarget.value)}
                  className="rounded-lg border border-ads-border px-2 py-1"
                >
                  <option value="">None</option>
                  {baselineCandidates.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {error ? <p className="text-[12px] text-red-700">{error}</p> : null}
            <div className="flex justify-end gap-2">
              <Button size="sm" className={dialogSecondaryButtonClass} onClick={close}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="primary"
                className={dialogPrimaryButtonClass}
                disabled={(tool.itkTool === "compareImages" || tool.dwiTool === "register" || tool.dwiTool === "segmentStroke") && !baselineNodeId}
                onClick={() => void run()}
              >
                <Play className="h-3.5 w-3.5" />
                Run
              </Button>
            </div>
          </div>
        ) : phase === "saved" ? (
          <div className="space-y-3 px-6 pb-6">
            <p className="text-[12.5px] text-ads-text">Saved to Output:</p>
            <p className="font-mono text-[11.5px] text-ads-muted">{savedPath}</p>
            <div className="flex justify-end gap-2">
              <Button size="sm" className={dialogSecondaryButtonClass} onClick={() => setPhase("ready")}>
                Back to result
              </Button>
              <Button size="sm" variant="primary" className={dialogPrimaryButtonClass} onClick={close}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1" style={{ height: 420 }}>
              <SliceCanvas
                baseUrl={baseUrl ?? resultUrl}
                overlays={isMaskLikeOverlay && resultUrl && baseUrl ? [{ url: resultUrl, color: OVERLAY_PALETTE[0], label: "Result" }] : []}
              />
            </div>
            <DialogFooter>
              <span className={cn("mr-auto", cnBusy(busy))}>{busy ? progress || "working…" : statusLine}</span>
              <Button size="sm" className={dialogSecondaryButtonClass} onClick={close}>
                Close
              </Button>
              <Button size="sm" variant="primary" className={dialogPrimaryButtonClass} disabled={!resultUrl || busy} onClick={save}>
                <Save className="h-3.5 w-3.5" />
                Save to Output
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogPanel>
    </DialogOverlay>
  );
}

function cnBusy(busy: boolean): string {
  return busy ? "text-[11px] text-ads-muted" : "text-[11px] text-neutral-500";
}
